import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Mail, ArrowRight } from "lucide-react";
interface StepItem {
  number: string;
  title: string;
  description: string;
}
const steps: StepItem[] = [{
  number: "01",
  title: "Initial enquiry",
  description: "You reach out by email or through the contact form with details about your website, market, and main search priorities. Starting by email is completely welcome, with no obligation to book a sales call before discussing your project."
}, {
  number: "02",
  title: "Written scope and priorities",
  description: "I review your website and current visibility, then send a clear written proposal outlining priority focus areas, deliverables, and commercial terms before any work begins."
}, {
  number: "03",
  title: "Agreed communication and access",
  description: "I confirm practical communication methods that suit you, whether that is email updates or optional video calls. I request only the access needed for the agreed work—such as Google Search Console, analytics, or CMS access—which you control and can revoke at any time."
}, {
  number: "04",
  title: "Implementation and shared documents",
  description: "I work through the agreed technical, on-page, or content priorities, providing clear shared documents, developer briefs, or direct changes so you can see exactly what is being done."
}, {
  number: "05",
  title: "Reporting and next steps",
  description: "I share straightforward written reports based on Search Console and analytics, detailing completed work, organic performance observations, and prioritised next steps."
}];
export default function RemoteWorking() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="remote-working" aria-labelledby="remote-working-heading" ref={ref} className="bg-background py-20 lg:py-28 border-b border-border transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Intro & Direct Contact Note */}
          <div className={`lg:col-span-5 lg:sticky lg:top-28 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
              Independent Collaboration
            </p>
            <h2 id="remote-working-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              How I Work With You Remotely
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              I collaborate with clients across{" "}
              <Link to="/UkCitiesAreasPage" className="text-primary underline font-medium hover:text-primary/80">
                UK cities and areas
              </Link>{" "}
              and international English-speaking markets (see{" "}
              <Link to="/InternationalLocationsPage" className="text-primary underline font-medium hover:text-primary/80">
                international locations
              </Link>
              ) entirely remotely. Communication is direct, prompt, and structured around written clarity so you always know what is being worked on.
            </p>

            <div className="mt-8 p-6 rounded-xl bg-card border border-border">
              <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                No high-pressure sales calls
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                You can begin the conversation directly by email. I review your
                details thoroughly before agreeing on next steps, giving you time
                to evaluate recommendations without pressure.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="default" className="rounded-md">
                  <a href="mailto:contact@ye-consultancy.com">
                    <Mail className="w-4 h-4 mr-2" />
                    Email me about your project
                  </a>
                </Button>
                <Button asChild variant="outline" size="default" className="rounded-md">
                  <Link to="/ContactPage">
                    Contact form
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Ordered List of Steps */}
          <div className={`lg:col-span-7 transition-all duration-700 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <ol className="space-y-6 list-none p-0 m-0">
              {steps.map((step, index) => <li key={step.number} className="bg-card text-card-foreground border border-border rounded-xl p-6 sm:p-7 flex gap-5 items-start hover:border-primary/40 transition-colors">
                  <span className="font-mono text-sm font-bold text-primary px-2.5 py-1.5 rounded bg-secondary/60 shrink-0">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2 leading-snug">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}